import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Pencil, Trash2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import RichContent from "@/components/RichContent";
import ContentBlocks from "@/components/ContentBlocks";
import VideoEmbed from "@/components/VideoEmbed";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

const isVideo = (u) => /\.(mp4|webm|mov|ogg)$/i.test(u) || /youtu\.?be|vimeo/.test(u);

export default function Article() {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/articles/${id}`).then((r) => setA(r.data)).catch(() => setErr(true));
  }, [id]);

  const del = async () => {
    try { await api.delete(`/articles/${id}`); toast.success("Artículo eliminado"); nav("/noticiero"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-ink-500">Artículo no encontrado.</div>;
  if (!a) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-ink-400">Cargando…</div>;

  return (
    <article className="max-w-3xl mx-auto px-5 lg:px-8 pt-24 pb-12">
      <Link to="/noticiero" className="inline-flex items-center gap-2 text-ink-500 hover:text-nasa-blue text-sm font-semibold mb-8" data-testid="back-noticiero">
        <ArrowLeft size={16} /> El Noticiero
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="eyebrow text-nasa-blue flex items-center gap-2 mb-4"><Calendar size={13} /> {fmtDate(a.created_at)}</span>
          <h1 className="font-serif-sci font-bold tracking-tight text-4xl sm:text-5xl leading-tight text-nasa-blue">{a.title}</h1>
          {a.excerpt && <p className="text-ink-700 mt-4 text-lg">{a.excerpt}</p>}
        </div>
        {isAdmin && (
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="icon" className="border-paper-300 rounded-full" onClick={() => nav(`/admin/articulo/${id}`)} data-testid="edit-article-btn"><Pencil size={16} /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="outline" size="icon" className="border-nasa-red text-nasa-red hover:bg-nasa-red hover:text-white rounded-full" data-testid="delete-article-btn"><Trash2 size={16} /></Button></AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader><AlertDialogTitle>¿Eliminar artículo?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-nasa-red hover:bg-nasa-reddark" onClick={del} data-testid="confirm-delete-article">Eliminar</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {a.cover_image && <img src={mediaUrl(a.cover_image)} alt={a.title} className="w-full border border-paper-200 mt-8 object-cover max-h-96" />}

      {a.blocks?.length ? (
        <div className="mt-10"><ContentBlocks blocks={a.blocks} textClassName="article-body" /></div>
      ) : (
        <>
          {a.content && <div className="mt-10"><RichContent html={a.content} className="article-body" /></div>}

          {a.media?.length > 0 && (
            <div className="mt-12 space-y-6">
              <div className="flex items-center gap-3"><span className="accent-rule" /><span className="eyebrow text-nasa-blue">GALERÍA</span></div>
              {a.media.map((m, i) => (
                isVideo(m) ? <VideoEmbed key={`${m}-${i}`} url={m} /> : <img key={`${m}-${i}`} src={mediaUrl(m)} alt="" className="w-full border border-paper-200" />
              ))}
            </div>
          )}
        </>
      )}
    </article>
  );
}
